import { motion } from 'framer-motion';

// ── Heraldic field standard for each sovereign ──
const KINGDOM_CONFIG = {
  Italy:   { glow: 'rgba(0,200,90,0.5)',   cloth: ['#009246', '#f3f4f6', '#CE2B37'], rim: '#0f9d58', crest: '⚜' },
  Spain:   { glow: 'rgba(255,170,0,0.5)',  cloth: ['#c60b1e', '#ffc400', '#c60b1e'], rim: '#e0a800', crest: '🦁' },
  France:  { glow: 'rgba(90,130,255,0.55)',cloth: ['#002395', '#f3f4f6', '#ED2939'], rim: '#3b5bdb', crest: '⚜' },
  England: { glow: 'rgba(255,80,90,0.5)',  cloth: ['#C8102E', '#f3f4f6', '#C8102E'], rim: '#C8102E', crest: '🦁' },
  Unknown: { glow: 'rgba(251,191,36,0.4)', cloth: ['#92400e', '#fbbf24', '#92400e'], rim: '#fbbf24', crest: '♛' },
};

// ── An embossed metal coin carrying one stat ──
const StatMedallion = ({ imgSrc, value, size = 'md', tone = '#ffd700' }) => {
  const cfg = {
    xs: { coin: 30, icon: 17, fs: 9.5 },
    sm: { coin: 40, icon: 23, fs: 12 },
    md: { coin: 52, icon: 30, fs: 15 },
  }[size] ?? { coin: 52, icon: 30, fs: 15 };

  return (
    <div style={{ position: 'relative', width: cfg.coin, height: cfg.coin, flexShrink: 0 }}>
      {/* coin body */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'radial-gradient(circle at 34% 28%, #5a4a26 0%, #2a2110 52%, #100c05 100%)',
        boxShadow: `inset 0 2px 3px rgba(255,225,150,0.28), inset 0 -3px 6px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.85), 0 0 0 1px ${tone}55`,
      }} />
      {/* engraved icon */}
      <img
        src={imgSrc} alt=""
        style={{
          position: 'absolute', top: '26%', left: '50%', transform: 'translateX(-50%)',
          width: cfg.icon, height: cfg.icon, objectFit: 'contain',
          filter: `drop-shadow(0 0 5px ${tone}aa) drop-shadow(0 2px 3px rgba(0,0,0,0.9))`,
        }}
        draggable={false}
      />
      {/* value struck into the lower rim */}
      <div style={{
        position: 'absolute', bottom: '3%', left: '50%', transform: 'translateX(-50%)',
        fontSize: cfg.fs, fontWeight: 900, color: tone, fontFamily: "'Cinzel',serif",
        lineHeight: 1, textShadow: '0 1px 2px rgba(0,0,0,1), 0 0 8px rgba(255,200,60,0.5)',
      }}>
        {value}
      </div>
    </div>
  );
};

// ── The hanging banner cloth above the plaque ──
const BannerCloth = ({ cloth, width, height, crest }) => (
  <div style={{ position: 'relative', width, height, flexShrink: 0, filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.85))' }}>
    <svg viewBox="0 0 60 90" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <linearGradient id={`cloth-${cloth.join('')}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={cloth[0]} />
          <stop offset="33%" stopColor={cloth[0]} />
          <stop offset="34%" stopColor={cloth[1]} />
          <stop offset="66%" stopColor={cloth[1]} />
          <stop offset="67%" stopColor={cloth[2]} />
          <stop offset="100%" stopColor={cloth[2]} />
        </linearGradient>
        <linearGradient id="cloth-shade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.28)" />
          <stop offset="55%" stopColor="rgba(0,0,0,0)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.55)" />
        </linearGradient>
      </defs>
      <path d="M2 2 H58 V64 L30 86 L2 64 Z" fill={`url(#cloth-${cloth.join('')})`} />
      <path d="M2 2 H58 V64 L30 86 L2 64 Z" fill="url(#cloth-shade)" />
      <path d="M2 2 H58 V64 L30 86 L2 64 Z" fill="none" stroke="rgba(255,215,0,0.65)" strokeWidth="2" />
      <text x="30" y="42" textAnchor="middle" fontSize="24" fill="rgba(0,0,0,0.45)">{crest}</text>
    </svg>
  </div>
);

// ── Main plaque ──
export const PlayerBox = ({ player, isActive, isPrisoned, size = 'desktop' }) => {
  if (!player) return null;
  const kingdom = player?.kingdom || 'Unknown';
  const cfg = KINGDOM_CONFIG[kingdom] || KINGDOM_CONFIG.Unknown;

  const d = {
    desktop: { w: 278, statSz: 'md', nameSz: 12, pad: '9px 14px 11px', gap: 14, banner: [34, 51] },
    self:    { w: 296, statSz: 'md', nameSz: 13, pad: '10px 16px 12px', gap: 16, banner: [38, 57] },
    top:     { w: 208, statSz: 'sm', nameSz: 10, pad: '7px 10px 9px',  gap: 10, banner: [26, 39] },
    side:    { w: 84,  statSz: 'xs', nameSz: 8,  pad: '6px 5px 8px',   gap: 6,  banner: [20, 30] },
  }[size] ?? { w: 278, statSz: 'md', nameSz: 12, pad: '9px 14px 11px', gap: 14, banner: [34, 51] };

  const isSide = size === 'side';
  const isSelf = size === 'self';

  return (
    <div style={{ position: 'relative', width: d.w, transformStyle: 'preserve-3d' }}>
      {/* ── Standard planted behind the plaque ── */}
      {!isSide && (
        <div style={{
          position: 'absolute', top: -d.banner[1] + 8, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: 6, zIndex: 0,
        }}>
          <motion.div
            animate={isActive ? { rotate: [-2.5, 2.5, -2.5] } : { rotate: [-1, 1, -1] }}
            transition={{ duration: isActive ? 1.6 : 3.4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transformOrigin: 'top center', opacity: isPrisoned ? 0.32 : 1, filter: isPrisoned ? 'grayscale(1)' : 'none' }}
          >
            <BannerCloth cloth={cfg.cloth} width={d.banner[0]} height={d.banner[1]} crest={cfg.crest} />
          </motion.div>
        </div>
      )}

      {/* ── Forged plaque body ── */}
      <div style={{
        position: 'relative', zIndex: 1, borderRadius: 13, overflow: 'hidden',
        background: `
          radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255,220,150,0.10) 0%, transparent 65%),
          linear-gradient(162deg, #2a2415 0%, #16110a 42%, #211a10 72%, #0c0905 100%)`,
        boxShadow: isActive
          ? `0 0 0 2px rgba(255,215,0,0.9), 0 0 30px ${cfg.glow}, 0 0 60px ${cfg.glow}, 0 10px 26px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,235,180,0.16)`
          : isPrisoned
            ? '0 0 0 1px rgba(70,62,46,0.45), 0 6px 16px rgba(0,0,0,0.8)'
            : `0 0 0 1px rgba(255,215,0,0.26), 0 0 14px ${cfg.glow}, 0 8px 22px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,235,180,0.1)`,
      }}>
        {/* polished metal sheen */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '44%', background: 'linear-gradient(to bottom, rgba(255,240,200,0.075), transparent)', pointerEvents: 'none', zIndex: 5 }} />
        <div style={{ position: 'absolute', top: '6%', left: 0, width: '20%', bottom: '6%', background: 'linear-gradient(to right, rgba(255,255,255,0.04), transparent)', pointerEvents: 'none', zIndex: 5 }} />

        <div style={{ padding: d.pad, position: 'relative', zIndex: 1 }}>
          {/* engraved name */}
          <div style={{
            textAlign: 'center', marginBottom: 6,
            fontSize: d.nameSz, fontWeight: 700,
            color: isPrisoned ? '#5c5443' : '#f0dfa0',
            fontFamily: "'Cinzel',serif", letterSpacing: '0.06em',
            textShadow: isPrisoned ? 'none' : '0 1px 0 rgba(0,0,0,0.95), 0 0 12px rgba(255,215,0,0.45)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {player.username}
          </div>

          <div style={{ height: 1, background: `linear-gradient(to right, transparent, ${cfg.rim}88, transparent)`, marginBottom: 7 }} />

          {/* stat medallions */}
          <div style={{
            display: 'flex', flexDirection: isSide ? 'column' : 'row',
            alignItems: 'center', justifyContent: 'center', gap: d.gap,
          }}>
            <StatMedallion imgSrc="/images/icons/crown.png"  value={player.crowns}  size={d.statSz} tone="#ffd700" />
            <StatMedallion imgSrc="/images/icons/shield.png" value={player.shields} size={d.statSz} tone="#93c5fd" />
            <StatMedallion imgSrc="/images/icons/jolly.png"  value={player.jolly}   size={d.statSz} tone="#d8b4fe" />
          </div>
        </div>

        {/* turn badge */}
        {isActive && !isPrisoned && !isSide && (
          <motion.div
            animate={{ opacity: [0.6, 1, 0.6], boxShadow: ['0 0 6px rgba(255,215,0,0.35)', '0 0 20px rgba(255,215,0,1)', '0 0 6px rgba(255,215,0,0.35)'] }}
            transition={{ duration: 0.9, repeat: Infinity }}
            style={{
              position: 'absolute', top: 6, right: 8, zIndex: 10,
              background: 'rgba(255,215,0,0.16)', border: '1px solid rgba(255,215,0,0.8)',
              borderRadius: 6, padding: '2px 8px',
              fontSize: size === 'top' ? 7 : 8.5, fontWeight: 900, color: '#ffd700',
              letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Cinzel',serif",
            }}
          >
            {isSelf ? 'Tocca a te' : 'Turno'}
          </motion.div>
        )}

        {/* active rim */}
        {isActive && !isPrisoned && (
          <motion.div
            animate={{ opacity: [0.3, 0.95, 0.3] }}
            transition={{ duration: 0.9, repeat: Infinity }}
            style={{ position: 'absolute', inset: 0, borderRadius: 13, border: '2px solid rgba(255,215,0,0.8)', pointerEvents: 'none', zIndex: 6 }}
          />
        )}

        {/* side marker */}
        {isActive && !isPrisoned && isSide && (
          <motion.div
            animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.4, 0.8] }}
            transition={{ duration: 0.9, repeat: Infinity }}
            style={{ position: 'absolute', bottom: 4, right: 4, width: 6, height: 6, borderRadius: '50%', background: '#ffd700', boxShadow: '0 0 10px #ffd700', zIndex: 10 }}
          />
        )}

        {/* ── Thrown in the dungeon ── */}
        {isPrisoned && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 8, background: 'rgba(0,0,0,0.78)', borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'space-evenly', padding: '0 6px' }}>
              {[...Array(7)].map((_, i) => (
                <div key={i} style={{ width: 3, background: 'linear-gradient(to right, #23201a, #8a8070 35%, #6a6152 55%, #1a1712)', borderRadius: 2, boxShadow: '0 0 4px rgba(0,0,0,0.9)' }} />
              ))}
            </div>
            <span data-testid="imprisoned-label" style={{
              position: 'relative', zIndex: 1, fontSize: isSide ? 7 : 9, fontWeight: 900,
              color: '#b9b0a0', letterSpacing: '0.14em', textTransform: 'uppercase',
              background: 'rgba(0,0,0,0.82)', padding: '3px 8px', borderRadius: 4,
              border: '1px solid rgba(140,130,110,0.35)', fontFamily: "'Cinzel',serif",
            }}>
              {isSide ? 'IMP' : '⛓ Prigioniero'}
            </span>
          </div>
        )}
      </div>

      {/* ── Ground shadow so the plaque stands on the field ── */}
      <div style={{
        position: 'absolute', left: '10%', right: '10%', bottom: -8, height: 12,
        background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.85), transparent 72%)',
        filter: 'blur(5px)', zIndex: 0, pointerEvents: 'none',
      }} />
    </div>
  );
};

export default PlayerBox;
